import React, { useState, useEffect } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Sparkles, Clock, ArrowLeft, Loader2, Tag } from 'lucide-react';
import { Helmet } from 'react-helmet';
import Header from '../../../components/navigation/Header';
import { supabase } from '../../../lib/supabase';

const BlogPostPage = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    const fetchPost = async () => {
      setLoading(true);
      try {
        const { data, error } = await supabase?.from('blog_posts')?.select('*')?.eq('slug', slug)?.eq('status', 'published')?.maybeSingle();

        if (error) throw error;
        if (!data) { setNotFound(true); setLoading(false); return; }
        setPost(data);

        // Fetch related posts (same category, different slug)
        const { data: relatedData } = await supabase?.from('blog_posts')?.select('id, title, slug, cover_image_url, cover_image_alt, category, read_time, published_at')?.eq('status', 'published')?.eq('category', data?.category)?.neq('slug', slug)?.limit(2);
        setRelated(relatedData || []);
      } catch (err) {
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    };
    fetchPost();
  }, [slug]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr)?.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <Loader2 size={32} className="animate-spin text-violet-500" />
      </div>
    );
  }

  if (notFound) return <Navigate to="/blog" replace />;

  const postUrl = `https://angle.dating/blog/${post?.slug}`;
  const postImage = post?.cover_image_url || 'https://angle.dating/assets/images/3-top-images-1772836145108.png';
  const postDescription = post?.excerpt || `Read ${post?.title} on the Angle! Blog.`;

  return (
    <div className="min-h-screen bg-white font-body">
      <Helmet>
        <title>{post?.title ? `${post?.title} | Angle! Blog` : 'Angle! Blog'}</title>
        <meta name="description" content={postDescription} />
        <link rel="canonical" href={postUrl} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={postUrl} />
        <meta property="og:title" content={post?.title || 'Angle! Blog'} />
        <meta property="og:description" content={postDescription} />
        <meta property="og:image" content={postImage} />
        <meta property="og:site_name" content="Angle!" />
        {post?.published_at && <meta property="article:published_time" content={post?.published_at} />}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={post?.title || 'Angle! Blog'} />
        <meta name="twitter:description" content={postDescription} />
        <meta name="twitter:image" content={postImage} />
        {post && <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BlogPosting",
          "headline": post?.title,
          "description": post?.excerpt || postDescription,
          "image": postImage,
          "url": postUrl,
          "datePublished": post?.published_at,
          "dateModified": post?.updated_at || post?.published_at,
          "author": {
            "@type": "Organization",
            "name": "Angle!",
            "url": "https://angle.dating"
          },
          "publisher": {
            "@type": "Organization",
            "name": "Angle!",
            "logo": {
              "@type": "ImageObject",
              "url": "https://angle.dating/assets/images/favicon_48x48-1783289233457.jpg"
            }
          },
          "mainEntityOfPage": {
            "@type": "WebPage",
            "@id": postUrl
          }
        })}</script>}
      </Helmet>
      <Header user={null} onNavigate={() => {}} onPurchaseCredits={() => {}} onLogout={() => {}} />
      {/* Hero Banner */}
      <div className="relative w-full overflow-hidden" style={{ paddingTop: '64px' }}>
        <div className="relative h-52 md:h-64 bg-gradient-to-br from-gray-900 via-violet-950 to-gray-900 flex items-end">
          <div className="absolute inset-0 bg-gradient-to-r from-violet-900/40 to-cyan-900/20" />
          <div className="absolute top-6 right-1/4 w-48 h-48 bg-violet-600/20 rounded-full blur-3xl" />
          <div className="absolute bottom-0 left-1/3 w-64 h-32 bg-cyan-500/10 rounded-full blur-3xl" />
          <div className="relative z-10 max-w-5xl mx-auto px-6 pb-8 w-full">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-violet-600 to-cyan-500 flex items-center justify-center">
                <Sparkles size={14} className="text-white" />
              </div>
              <span className="text-white font-bold text-lg" style={{ fontFamily: 'Crimson Text, serif' }}>Angle!</span>
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-white mb-1" style={{ fontFamily: 'Crimson Text, serif' }}>
              The Angle Blog
            </h1>
            <p className="text-violet-200/80 text-sm">
              Photo tips, dating advice, and AI insights to help you put your best face forward.
            </p>
          </div>
        </div>
      </div>
      {/* Article */}
      <main className="max-w-3xl mx-auto px-6 py-12">
        <Link
          to="/blog"
          className="inline-flex items-center gap-1.5 text-sm text-violet-600 hover:text-violet-800 font-medium mb-8 transition-colors"
        >
          <ArrowLeft size={14} /> Back to Blog
        </Link>

        <motion.article
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
        >
          {/* Category + meta */}
          <div className="flex items-center gap-3 mb-4 flex-wrap">
            <span className="text-xs font-semibold text-violet-600 bg-violet-50 px-2.5 py-1 rounded-full">
              {post?.category}
            </span>
            <span className="flex items-center gap-1 text-xs text-gray-400">
              <Clock size={11} /> {post?.read_time}
            </span>
            <span className="text-xs text-gray-400">{formatDate(post?.published_at)}</span>
          </div>

          {/* Title */}
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900 leading-tight mb-6" style={{ fontFamily: 'Crimson Text, serif' }}>
            {post?.title}
          </h1>

          {/* Hero image */}
          {post?.cover_image_url && (
            <div className="rounded-2xl overflow-hidden mb-8 aspect-[16/9] bg-gray-100">
              <img
                src={post?.cover_image_url}
                alt={post?.cover_image_alt || post?.title}
                className="w-full h-full object-cover"
              />
            </div>
          )}

          {/* Tags */}
          {post?.tags?.length > 0 && (
            <div className="flex items-center gap-2 flex-wrap mb-6">
              <Tag size={12} className="text-gray-400" />
              {post?.tags?.map(tag => (
                <span key={tag} className="text-xs text-gray-500 bg-gray-100 px-2.5 py-1 rounded-full">
                  #{tag}
                </span>
              ))}
            </div>
          )}

          {/* Article body — rendered HTML */}
          <div
            className="blog-post-content"
            dangerouslySetInnerHTML={{ __html: post?.content }}
          />
        </motion.article>

        {/* Related posts */}
        {related?.length > 0 && (
          <section className="mt-16 pt-10 border-t border-gray-100">
            <h3 className="text-lg font-bold text-gray-900 mb-6" style={{ fontFamily: 'Crimson Text, serif' }}>
              More from the Blog
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              {related?.map((rel) => (
                <Link
                  key={rel?.slug}
                  to={`/blog/${rel?.slug}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group flex gap-4 p-4 rounded-xl border border-gray-100 hover:border-violet-200 hover:shadow-sm transition-all"
                >
                  <div className="w-20 h-16 rounded-lg overflow-hidden flex-shrink-0 bg-gray-100">
                    {rel?.cover_image_url ? (
                      <img
                        src={rel?.cover_image_url}
                        alt={rel?.cover_image_alt || rel?.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        loading="lazy"
                      />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-violet-100 to-cyan-50 flex items-center justify-center">
                        <Sparkles size={16} className="text-violet-300" />
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <span className="text-xs text-violet-600 font-semibold">{rel?.category}</span>
                    <p className="text-sm font-semibold text-gray-800 leading-snug mt-0.5 line-clamp-2 group-hover:text-violet-700 transition-colors">
                      {rel?.title}
                    </p>
                    <span className="text-xs text-gray-400 mt-1 block">{formatDate(rel?.published_at)}</span>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}
      </main>
      {/* Footer */}
      <footer className="bg-gray-950 text-gray-400 py-14 px-6 mt-8">
        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-10 mb-12">
            <div className="col-span-2 md:col-span-1">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-violet-600 to-cyan-500 flex items-center justify-center">
                  <Sparkles size={15} className="text-white" />
                </div>
                <span className="text-white font-bold text-lg" style={{ fontFamily: 'Crimson Text, serif' }}>Angle!</span>
              </div>
              <p className="text-sm text-gray-500 leading-relaxed">AI-Powered Photo Rankings</p>
            </div>
            <div>
              <h4 className="text-white font-semibold text-sm mb-4">Product</h4>
              <ul className="flex flex-col gap-2.5 text-sm">
                <li><Link to="/purchase-credits" className="hover:text-gray-200 transition-colors">Pricing</Link></li>
                <li><Link to="/frequently-asked-questions" className="hover:text-gray-200 transition-colors">FAQ</Link></li>
                <li><Link to="/blog" className="hover:text-gray-200 transition-colors">Blog</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-semibold text-sm mb-4">Company</h4>
              <ul className="flex flex-col gap-2.5 text-sm">
                <li><Link to="/privacy-policy" className="hover:text-gray-200 transition-colors">Privacy</Link></li>
                <li><Link to="/terms-of-service" className="hover:text-gray-200 transition-colors">Terms</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-semibold text-sm mb-4">Account</h4>
              <ul className="flex flex-col gap-2.5 text-sm">
                <li><Link to="/auth" className="hover:text-gray-200 transition-colors">Sign In</Link></li>
                <li><Link to="/auth" className="hover:text-gray-200 transition-colors">Sign Up Free</Link></li>
                <li><Link to="/purchase-credits" className="hover:text-gray-200 transition-colors">Buy Credits</Link></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-6 flex flex-col sm:flex-row items-center justify-between gap-3">
            <p className="text-xs text-gray-600">© {new Date()?.getFullYear()} Angle! All rights reserved.</p>
            <p className="text-xs text-gray-600">Made with ✨ AI</p>
          </div>
        </div>
      </footer>
      <style>{`
        .blog-post-content { color: #374151; line-height: 1.75; font-size: 1rem; }
        .blog-post-content h1 { font-size: 1.75rem; font-weight: 700; margin: 1.5rem 0 0.75rem; font-family: 'Crimson Text', serif; color: #111827; }
        .blog-post-content h2 { font-size: 1.4rem; font-weight: 700; margin: 1.5rem 0 0.75rem; font-family: 'Crimson Text', serif; color: #111827; }
        .blog-post-content h3 { font-size: 1.15rem; font-weight: 600; margin: 1.25rem 0 0.5rem; color: #1f2937; }
        .blog-post-content p { margin-bottom: 1rem; }
        .blog-post-content ul { list-style: disc; padding-left: 1.5rem; margin-bottom: 1rem; }
        .blog-post-content ol { list-style: decimal; padding-left: 1.5rem; margin-bottom: 1rem; }
        .blog-post-content li { margin-bottom: 0.25rem; }
        .blog-post-content blockquote { border-left: 4px solid #7c3aed; padding-left: 1rem; color: #6b7280; font-style: italic; margin: 1.25rem 0; background: #f5f3ff; padding: 0.75rem 1rem; border-radius: 0 0.5rem 0.5rem 0; }
        .blog-post-content pre { background: #1f2937; color: #d1fae5; padding: 1rem; border-radius: 0.75rem; font-family: monospace; font-size: 0.875rem; overflow-x: auto; margin: 1rem 0; }
        .blog-post-content code { background: #f3f4f6; color: #7c3aed; padding: 0.15rem 0.4rem; border-radius: 0.25rem; font-size: 0.875em; font-family: monospace; }
        .blog-post-content pre code { background: transparent; color: inherit; padding: 0; }
        .blog-post-content a { color: #7c3aed; text-decoration: underline; }
        .blog-post-content a:hover { color: #5b21b6; }
        .blog-post-content img { max-width: 100%; border-radius: 0.75rem; margin: 1rem 0; }
        .blog-post-content strong { font-weight: 700; color: #111827; }
        .blog-post-content em { font-style: italic; }
      `}</style>
    </div>
  );
};

export default BlogPostPage;
